import React, { useState, useEffect } from 'react';
import apiClient from '../api/client';
import { type Plugin } from "../context/PluginContext";
import { PluginCard } from "../components/common/PluginCard";
import { PluginCardSkeleton } from "../components/common/PluginCardSkeleton";
import { Link } from 'react-router-dom';
import Slider from "react-slick";
import { useWindowSize } from '../hooks/useWindowSize';
import { ReviewCardSkeleton } from '../components/common/ReviewCardSkeleton';
import { PartnerSkeleton } from '../components/common/PartnerSkeleton';
import { APP_CONFIG } from '../config/constants';

// Tipos
interface StoreReview {
    id: number;
    authorName: string;
    rating: number;
    text: string;
    avatarUrl?: string;
}


interface CustomerServer {
    serverName: string;
    serverWebsite: string | null;
    serverLogoUrl: string;
    username: string;
    pluginsCount: number;
}

// Novo: tipo para as estatísticas da homepage
interface Stats {
    activeServers: number | null;
    pluginsAvailable: number | null;
    supportText?: string;
}

// Componente de Estrelas
const StarRating = ({ rating = 5 }: { rating: number }) => (
    <div className="flex items-center gap-1">
        {Array.from({ length: 5 }).map((_, index) => (
            <svg key={index} className={`w-5 h-5 ${index < rating ? 'text-yellow-400' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
        ))}
    </div>
);

// Ícones para a seção "Por que escolher a loja?"
const RecognitionIcon = () => (
    <div className="flex-shrink-0 bg-brand-blue/10 p-3 rounded-full">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-brand-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
        </svg>
    </div>
);
const CompatibilityIcon = () => (
    <div className="flex-shrink-0 bg-brand-blue/10 p-3 rounded-full">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-brand-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 14v6m-3-3h6M6 10h2a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v2a2 2 0 002 2zm10 0h2a2 2 0 002-2V6a2 2 0 00-2-2h-2a2 2 0 00-2 2v2a2 2 0 002 2zM6 20h2a2 2 0 002-2v-2a2 2 0 00-2-2H6a2 2 0 00-2 2v2a2 2 0 002 2z" />
        </svg>
    </div>
);
const SecurityIcon = () => (
    <div className="flex-shrink-0 bg-brand-blue/10 p-3 rounded-full">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-brand-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
    </div>
);
const SupportIcon = () => (
    <div className="flex-shrink-0 bg-brand-blue/10 p-3 rounded-full">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-brand-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a2 2 0 01-2-2V10a2 2 0 012-2h8z" />
        </svg>
    </div>
);

const features = [
    {
        icon: <RecognitionIcon />,
        title: "Reconhecimento",
        description: "Somos uma loja fundada em 2014, reconhecida pela excelência e confiança no mercado.\n" +
            "Trabalhamos com um padrão premium, priorizando qualidade em vez de quantidade, a um preço justo.\n" +
            "Esse cuidado e dedicação são o que nos diferencia verdadeiramente da concorrência."
    },
    {
        icon: <CompatibilityIcon />,
        title: "Compatibilidade",
        description: "Nossos plugins são compatíveis com BungeeCord, Velocity-Snap (Suporte parcial) e Spigot. Desde a versão 1.8.8 do Minecraft, mantemos compatibilidade contínua, acompanhando cada atualização para garantir uma experiência estável e atualizada para todos os servidores."
    },
    {
        icon: <SecurityIcon />,
        title: "Segurança e Melhorias",
        description: "Com atualizações constantes, nossos plugins oferecem correções, melhorias de desempenho e novos recursos, trazendo, assim, estabilidade e inovação para o seu servidor."
    },
    {
        icon: <SupportIcon />,
        title: "Atendimento",
        description: "Nosso atendimento é o mais rápido do mercado. Com uma equipe eficiente de segunda a sexta-feira (com exceções nos finais de semana), atendemos clientes com prioridade, garantindo agilidade em cada atendimento."
    }
];

export default function HomePage() {
    const [featuredPlugins, setFeaturedPlugins] = useState<Plugin[]>([]);
    const [reviews, setReviews] = useState<StoreReview[]>([]);
    const [customerServers, setCustomerServers] = useState<CustomerServer[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [sliderKey, setSliderKey] = useState(0);
    const size = useWindowSize();
    const [stats, setStats] = useState<Stats | null>(null);

    useEffect(() => {
        async function fetchHomePageData() {
            setLoading(true);
            setError(null);

            try {
                // Buscar plugins em destaque
                const pluginsRes = await apiClient.get('/storefront/plugins', {
                    params: { limit: 8, isFeatured: 'true' }
                });
                setFeaturedPlugins(pluginsRes.data.plugins || []);
            } catch (err: any) {
                console.error('Erro ao buscar plugins:', err);
                setError('Falha ao carregar plugins');
            }

            try {
                // Buscar reviews
                const reviewsRes = await apiClient.get('/storefront/reviews', {
                    params: { limit: 10 }
                });
                setReviews(reviewsRes.data || []);
            } catch (err: any) {
                console.error('Erro ao buscar reviews:', err);
            }

            try {
                // Buscar servidores de clientes
                const serversRes = await apiClient.get('/storefront/customer-servers', {
                    params: { limit: 20 }
                });
                setCustomerServers(serversRes.data || []);
            } catch (err: any) {
                console.error('Erro ao buscar servidores:', err);
                // Não mostra erro se não houver servidores, apenas deixa vazio
            }

            try {
                // Buscar estatísticas da homepage (active servers, plugins disponíveis, suporte)
                const statsRes = await apiClient.get('/storefront/stats');
                const d = statsRes.data || {};

                // Proteção: garantir que valores numéricos sejam realmente números
                const activeServers = d && typeof d.activeServers !== 'undefined' ? Number(d.activeServers) : null;
                const pluginsAvailable = d && typeof d.pluginsAvailable !== 'undefined' ? Number(d.pluginsAvailable) : null;
                const supportText = d && typeof d.supportText === 'string' ? d.supportText : undefined;

                setStats({ activeServers: Number.isFinite(activeServers) ? activeServers : null, pluginsAvailable: Number.isFinite(pluginsAvailable) ? pluginsAvailable : null, supportText });
            } catch (err: any) {
                // Se o endpoint não existir ainda, usamos os valores estáticos como fallback
                console.error('Erro ao buscar estatísticas:', err);
            }

            setLoading(false);
        }

        fetchHomePageData();
    }, []);

    useEffect(() => {
        setSliderKey(prevKey => prevKey + 1);
    }, [size.width]);

    // Configurações dos Sliders
    let reviewSlidesToShow = 3;
    if (size.width && size.width < 1024) reviewSlidesToShow = 2;
    if (size.width && size.width < 640) reviewSlidesToShow = 1;
    const reviewSettings = { dots: true, arrows: false, infinite: reviews.length > reviewSlidesToShow, speed: 500, autoplaySpeed: 5000, cssEase: "ease-in-out", slidesToShow: reviewSlidesToShow, slidesToScroll: reviewSlidesToShow };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-gray-50">
            {/* Hero Section Moderno */}
            <section className="relative bg-gradient-to-r from-brand-blue via-blue-600 to-blue-700 overflow-hidden">
                {/* Padrão decorativo de fundo */}
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute -top-24 -right-24 w-96 h-96 bg-white rounded-full"></div>
                    <div className="absolute top-1/2 left-1/4 w-64 h-64 bg-white rounded-full"></div>
                    <div className="absolute -bottom-32 -left-32 w-80 h-80 bg-white rounded-full"></div>
                </div>

                <div className="container mx-auto px-4 py-24 md:py-32 relative">
                    <div className="max-w-5xl mx-auto text-center">
                        {/* Badge */}
                        <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-8 border border-white/30">
                            <svg className="w-5 h-5 text-yellow-300" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                            <span className="text-white font-semibold text-sm">A melhor loja de plugins do Brasil</span>
                        </div>

                        {/* Título Principal */}
                        <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold text-white mb-6 leading-tight drop-shadow-2xl">
                            Transforme seu servidor
                            <br />
                            <span className="bg-gradient-to-r from-yellow-200 via-yellow-100 to-white bg-clip-text text-transparent">
                                Minecraft
                            </span>
                        </h1>

                        <p className="text-xl md:text-2xl text-blue-100 mb-10 max-w-3xl mx-auto leading-relaxed">
                            Plugins premium de alta qualidade para levar seu servidor ao próximo nível.
                            Economia, minigames, sistemas avançados e muito mais.
                        </p>

                        {/* Botões de CTA */}
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                            <Link
                                to="/plugins"
                                className="group inline-flex items-center gap-2 bg-white text-brand-blue font-bold py-4 px-8 rounded-xl text-lg hover:bg-blue-50 transition-all shadow-2xl hover:shadow-xl transform hover:-translate-y-1"
                            >
                                <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                </svg>
                                Explorar Plugins
                            </Link>
                            <Link
                                to="/plugins"
                                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white font-semibold py-4 px-8 rounded-xl text-lg hover:bg-white/20 transition-all border-2 border-white/30 hover:border-white/50"
                            >
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Ver Demonstração
                            </Link>
                        </div>

                        {/* Stats */}
                        <div className="grid grid-cols-3 gap-8 mt-16 max-w-3xl mx-auto">
                            <div className="text-center">
                                <div className="text-4xl font-extrabold text-white mb-2">
                                    {stats?.activeServers != null
                                        ? `${new Intl.NumberFormat('pt-BR').format(stats.activeServers)}+`
                                        : '500+'}
                                </div>
                                <div className="text-blue-200 text-sm">Servidores Ativos</div>
                            </div>
                            <div className="text-center">
                                <div className="text-4xl font-extrabold text-white mb-2">
                                    {stats?.pluginsAvailable != null
                                        ? `${new Intl.NumberFormat('pt-BR').format(stats.pluginsAvailable)}+`
                                        : '50+'}
                                </div>
                                <div className="text-blue-200 text-sm">Plugins Premium</div>
                            </div>
                            <div className="text-center">
                                <div className="text-4xl font-extrabold text-white mb-2">{stats?.supportText ?? '24/7'}</div>
                                <div className="text-blue-200 text-sm">Suporte Dedicado</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Ondinha decorativa */}
                <div className="absolute bottom-0 left-0 right-0">
                    <svg className="w-full h-12 text-gray-50" preserveAspectRatio="none" viewBox="0 0 1200 120" fill="currentColor">
                        <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z"></path>
                    </svg>
                </div>
            </section>

            {/* Seção de Plugins em Destaque */}
            <section className="container mx-auto px-4 py-16">
                <div className="text-center mb-12">
                    <div className="inline-flex items-center gap-2 bg-brand-blue/10 px-4 py-2 rounded-full mb-4">
                        <svg className="w-5 h-5 text-brand-blue" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                        <span className="text-brand-blue font-semibold text-sm">Mais Populares</span>
                    </div>
                    <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
                        Plugins em <span className="bg-gradient-to-r from-brand-blue to-blue-600 bg-clip-text text-transparent">Destaque</span>
                    </h2>
                    <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                        Descubra os plugins mais populares e bem avaliados pela nossa comunidade
                    </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
                    {loading ? (
                        Array.from({ length: 8 }).map((_, index) => <PluginCardSkeleton key={index} />)
                    ) : (
                        featuredPlugins.map(plugin => <PluginCard key={plugin.id} plugin={plugin} />)
                    )}
                </div>
                <div className="text-center mt-12">
                    <Link
                        to="/plugins"
                        className="inline-flex items-center gap-2 bg-white border-2 border-brand-blue text-brand-blue font-bold py-3 px-8 rounded-xl hover:bg-brand-blue hover:text-white transition-all shadow-lg hover:shadow-xl"
                    >
                        Ver Todos os Plugins
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                    </Link>
                </div>
            </section>

            {/* Seção: Por que escolher nossa loja? */}
            <section className="container mx-auto px-4 py-16">
                <div className="text-center mb-12">
                    <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
                        Por que escolher a <span className="bg-gradient-to-r from-brand-blue to-blue-600 bg-clip-text text-transparent">{APP_CONFIG.storeName}?</span>
                    </h2>
                    <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                        Somos a escolha preferida de milhares de servidores em todo o Brasil
                    </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {features.map((feature, index) => (
                        <div key={index} className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-200 hover:border-brand-blue/50 transform hover:-translate-y-1">
                            <div className="flex gap-5 items-start">
                                <div className="flex-shrink-0 bg-gradient-to-br from-brand-blue to-blue-600 p-4 rounded-2xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                                    <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        {index === 0 && <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />}
                                        {index === 1 && <path strokeLinecap="round" strokeLinejoin="round" d="M17 14v6m-3-3h6M6 10h2a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v2a2 2 0 002 2zm10 0h2a2 2 0 002-2V6a2 2 0 00-2-2h-2a2 2 0 00-2 2v2a2 2 0 002 2zM6 20h2a2 2 0 002-2v-2a2 2 0 00-2-2H6a2 2 0 00-2 2v2a2 2 0 002 2z" />}
                                        {index === 2 && <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />}
                                        {index === 3 && <path strokeLinecap="round" strokeLinejoin="round" d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a2 2 0 01-2-2V10a2 2 0 012-2h8z" />}
                                    </svg>
                                </div>
                                <div className="flex-1">
                                    <h4 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-brand-blue transition-colors">{feature.title}</h4>
                                    <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Seção de Avaliações */}
            <section className="relative py-20 overflow-hidden">
                {/* Background decorativo */}
                <div className="absolute inset-0 bg-gradient-to-b from-blue-50/50 via-white to-blue-50/50"></div>
                <div className="absolute inset-0 opacity-5">
                    <div className="absolute top-20 left-10 w-72 h-72 bg-brand-blue rounded-full blur-3xl"></div>
                    <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-400 rounded-full blur-3xl"></div>
                </div>

                <div className="container mx-auto px-4 relative">
                    <div className="text-center mb-16">
                        <div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full mb-4 shadow-lg">
                            <svg className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                            <span className="text-gray-700 font-semibold text-sm">Depoimentos</span>
                        </div>
                        <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
                            O que nossos <span className="bg-gradient-to-r from-brand-blue to-blue-600 bg-clip-text text-transparent">clientes</span> dizem
                        </h2>
                        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                            Milhares de servidores confiam em nossos plugins. Veja o que eles têm a dizer
                        </p>
                    </div>

                    {loading ? (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto">
                            <ReviewCardSkeleton/>
                            <ReviewCardSkeleton className="hidden md:block"/>
                            <ReviewCardSkeleton className="hidden lg:block"/>
                        </div>
                    ) : error ? (
                        <p className="text-red-500 text-center">{error}</p>
                    ) : (
                        <div className="max-w-7xl mx-auto">
                            <Slider key={sliderKey} {...reviewSettings}>
                                {reviews.map((review) => (
                                    <div key={review.id} className="px-3 sm:px-4 h-full">
                                        <div className="relative bg-white p-8 rounded-2xl h-full flex flex-col shadow-xl hover:shadow-2xl transition-all duration-300 border border-gray-200 group hover:-translate-y-1">
                                            {/* Quote icon */}
                                            <div className="absolute top-6 right-6 text-brand-blue/10 group-hover:text-brand-blue/20 transition-colors">
                                                <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                                                </svg>
                                            </div>

                                            <div className="flex items-center gap-3 mb-6">
                                                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-brand-blue to-blue-600 flex items-center justify-center shadow-lg overflow-hidden">
                                                    {review.avatarUrl ? (
                                                        <img
                                                            src={`${APP_CONFIG.uploadsBaseUrl}${review.avatarUrl}`}
                                                            alt={review.authorName}
                                                            className="w-full h-full object-cover"
                                                            onError={(e) => {
                                                                // Fallback para inicial do nome se a imagem não carregar
                                                                const target = e.currentTarget;
                                                                target.style.display = 'none';
                                                                const parent = target.parentElement;
                                                                if (parent && !parent.querySelector('span')) {
                                                                    const fallback = document.createElement('span');
                                                                    fallback.className = 'text-white font-bold text-lg';
                                                                    fallback.textContent = review.authorName.charAt(0);
                                                                    parent.appendChild(fallback);
                                                                }
                                                            }}
                                                        />
                                                    ) : (
                                                        <span className="text-white font-bold text-lg">{review.authorName.charAt(0)}</span>
                                                    )}
                                                </div>
                                                <div className="flex-1">
                                                    <p className="font-bold text-gray-900">{review.authorName}</p>
                                                    <div className="flex items-center gap-1 mt-1">
                                                        <StarRating rating={review.rating}/>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex-grow">
                                                <p className="text-gray-700 leading-relaxed text-base">
                                                    "{review.text}"
                                                </p>
                                            </div>

                                            {/* Bottom accent */}
                                            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-brand-blue to-blue-600 rounded-b-2xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                                        </div>
                                    </div>
                                ))}
                            </Slider>
                        </div>
                    )}
                </div>
            </section>

            {/* Seção de Servidores de Clientes */}
            <section className="container mx-auto px-4 py-16">
                <div className="text-center mb-12">
                    <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                        Servidores que <span className="bg-gradient-to-r from-brand-blue to-blue-600 bg-clip-text text-transparent">confiam</span> em nós
                    </h3>
                    <p className="text-gray-600">Conheça algumas comunidades que utilizam nossos plugins</p>
                </div>
                {loading ? (
                    <div className="flex justify-center items-center gap-x-8 sm:gap-x-16 gap-y-6">
                        {Array.from({length: 5}).map((_, i) => <PartnerSkeleton key={i}/>)}
                    </div>
                ) : error ? (
                    <p className="text-red-500 text-center">{error}</p>
                ) : customerServers.length === 0 ? (
                    <div className="text-center py-8">
                        <p className="text-gray-500">Nenhum servidor cadastrado ainda</p>
                    </div>
                ) : (
                    <div className="relative w-full max-w-5xl mx-auto">
                        <div className="bg-white shadow-xl rounded-3xl py-8 px-6 border border-gray-200">
                            <Slider
                                infinite={true}
                                speed={3000}
                                autoplay={true}
                                autoplaySpeed={0}
                                cssEase="linear"
                                slidesToShow={5}
                                slidesToScroll={1}
                                arrows={false}
                                pauseOnHover={true}
                                responsive={[
                                    {
                                        breakpoint: 1024,
                                        settings: {
                                            slidesToShow: 4
                                        }
                                    },
                                    {
                                        breakpoint: 768,
                                        settings: {
                                            slidesToShow: 3
                                        }
                                    },
                                    {
                                        breakpoint: 640,
                                        settings: {
                                            slidesToShow: 2
                                        }
                                    }
                                ]}
                            >
                                {customerServers.map((server, index) => (
                                    <div key={`${server.username}-${index}`} className="px-4">
                                        {server.serverWebsite ? (
                                            <a
                                                href={server.serverWebsite}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="block group"
                                            >
                                                <div className="flex flex-col items-center gap-2 hover:scale-110 transition-transform duration-300">
                                                    <img
                                                        src={`${APP_CONFIG.uploadsBaseUrl}${server.serverLogoUrl}`}
                                                        alt={server.serverName}
                                                        className="h-16 w-16 object-contain transition-all duration-300 rounded-lg"
                                                        onError={(e) => {
                                                            e.currentTarget.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="%23CBD5E0" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>';
                                                        }}
                                                    />
                                                </div>
                                            </a>
                                        ) : (
                                            <div className="flex flex-col items-center gap-2">
                                                <img
                                                    src={`${APP_CONFIG.uploadsBaseUrl}${server.serverLogoUrl}`}
                                                    alt={server.serverName}
                                                    className="h-16 w-16 object-contain transition-all duration-300 rounded-lg"
                                                    onError={(e) => {
                                                        e.currentTarget.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="%23CBD5E0" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>';
                                                    }}
                                                />
                                                <span className="text-xs text-gray-600 font-medium text-center">
                                                    {server.serverName}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </Slider>
                        </div>
                    </div>
                )}
            </section>

            {/* CTA Final */}
            <section className="container mx-auto px-4 py-16">
                <div className="relative bg-gradient-to-r from-brand-blue via-blue-600 to-blue-700 rounded-3xl overflow-hidden shadow-2xl">
                    {/* Background decorativo */}
                    <div className="absolute inset-0 opacity-10">
                        <div className="absolute -top-24 -right-24 w-96 h-96 bg-white rounded-full"></div>
                        <div className="absolute -bottom-32 -left-32 w-80 h-80 bg-white rounded-full"></div>
                    </div>

                    <div className="relative px-8 py-16 md:py-20 text-center">
                        <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">
                            Pronto para transformar seu servidor?
                        </h2>
                        <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
                            Junte-se aos servidores que já confiam em nossos trabalho
                        </p>
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                            <Link
                                to="/plugins"
                                className="inline-flex items-center gap-2 bg-white text-brand-blue font-bold py-4 px-8 rounded-xl text-lg hover:bg-blue-50 transition-all shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
                            >
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                </svg>
                                Começar Agora
                            </Link>
                            <Link
                                to="/conta"
                                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white font-semibold py-4 px-8 rounded-xl text-lg hover:bg-white/20 transition-all border-2 border-white/30"
                            >
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                                Criar Conta
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
